import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  Cloud,
  Users,
  FileText,
  AlertTriangle,
  Loader2,
  Pencil,
  Image,
  Download,
  FilePlus,
  Link as LinkIcon,
  Send,
  Copy,
  ExternalLink,
  X,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Textarea } from '@/app/components/ui/textarea';
import { Label } from '@/app/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/simple-toaster';
import {
  useDailyReport,
  useAmendDailyReport,
  useExportDailyReportPdf,
} from '@/hooks/useDailyReports';
import {
  useProject,
  useClient,
  usePortalAccesses,
  useCreatePortalAccess,
  useRegeneratePortalToken,
} from '@/hooks/use-projects';
import { usePermissions } from '@/hooks/use-permissions';
import { usePlatformModules } from '@/hooks/use-modules';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type {
  DailyReportPhoto,
  WeatherSnapshot,
  ClientPortalAccess,
} from '@/lib/types';

const DELAY_TYPE_LABELS: Record<string, string> = {
  weather: 'Weather',
  client: 'Client',
  safety: 'Safety',
  access: 'Access',
  equipment: 'Equipment',
  permit: 'Permit',
  rescue: 'Rescue',
  standby: 'Standby',
  other: 'Other',
};

function formatPhotoMeta(photo: DailyReportPhoto, timeZone: string) {
  const gps =
    photo.capture_latitude != null && photo.capture_longitude != null
      ? `GPS: ${photo.capture_latitude}, ${photo.capture_longitude}`
      : 'GPS: not provided';
  const capturedAt = photo.captured_at
    ? `Captured: ${formatDateTimeInTimeZone(photo.captured_at, timeZone)}`
    : 'Captured: not provided';
  return { gps, capturedAt };
}

function formatWeatherSnapshot(snapshot: WeatherSnapshot | null) {
  if (!snapshot) {
    return 'Weather at delay: not recorded';
  }
  const current = snapshot.current;
  return `Weather at delay: ${current.condition}, ${current.temperature_c}°C / ${current.temperature_f}°F, wind ${current.wind_speed_kmh} km/h (gust ${current.wind_gust_kmh}), precip ${current.precipitation_mm} mm — observed ${current.observed_time}`;
}

export function DailyReportDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id: projectIdParam, reportId } = useParams<{
    id: string;
    reportId: string;
  }>();
  const projectId = Number(projectIdParam);
  const rId = Number(reportId);
  const navigate = useNavigate();
  const { isModuleEnabled } = usePlatformModules();
  const billingEnabled = isModuleEnabled('projects');

  const { data: report, isLoading } = useDailyReport(rId);
  const { data: project, isLoading: isLoadingProject } = useProject(projectId);
  // Clients are Admin/Manager-only on the server; only they can share the
  // report with the client contact.
  const { canViewClients } = usePermissions();
  const { data: client, isLoading: isLoadingClient } = useClient(
    project?.client ?? 0,
    billingEnabled && canViewClients && project?.client != null
  );
  const {
    data: portalAccesses,
    isLoading: isLoadingPortalAccesses,
    isSuccess: portalAccessesLoaded,
  } = usePortalAccesses(
    { client: project?.client ?? 0 },
    billingEnabled && project?.client != null
  );
  const amendMutation = useAmendDailyReport();
  const exportPdfMutation = useExportDailyReportPdf();
  const createPortalAccess = useCreatePortalAccess();
  const regeneratePortalToken = useRegeneratePortalToken();

  const [showPortalLink, setShowPortalLink] = useState(false);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [amendReason, setAmendReason] = useState('');
  const [amendChanges, setAmendChanges] = useState('');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        Report not found.
      </div>
    );
  }

  const isSubmitted = !!report.submitted_at;

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/work/daily-reports/${projectId}`)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold">Daily Report</h1>
            <p className="text-xs text-muted-foreground">
              {formatDateOnly(report.date)}
              {report.submitted_at &&
                ` · Submitted ${formatDateTimeInTimeZone(report.submitted_at, timeZone)}`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {report.revision > 1 && (
              <Badge variant="outline" className="text-xs">
                v{report.revision}
              </Badge>
            )}
            {isSubmitted ? (
              <Badge variant="secondary">
                <Cloud className="h-3 w-3 mr-1" /> Submitted
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="text-amber-600 border-amber-300"
              >
                Draft
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Amendment link */}
        {report.amended_from && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
            <LinkIcon className="h-3.5 w-3.5" />
            <span>
              Revision of{' '}
              <button
                className="underline text-blue-600"
                onClick={() =>
                  navigate(
                    `/work/daily-reports/${projectId}/${report.amended_from}`
                  )
                }
              >
                previous version
              </button>
            </span>
          </div>
        )}

        {/* Date & Weather */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Date & Weather
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p>
              <span className="font-medium">Date:</span> {report.date}
            </p>
            <p>
              <span className="font-medium">Weather:</span>{' '}
              {report.weather || '—'}
            </p>
            {report.shift_start && (
              <p className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                <span className="font-medium">Shift:</span>{' '}
                {report.shift_start} – {report.shift_end}
              </p>
            )}
            {report.supervisor_name && (
              <p>
                <span className="font-medium">Supervisor:</span>{' '}
                {report.supervisor_name}
              </p>
            )}
            <p>
              <span className="font-medium">Reported by:</span>{' '}
              {report.reported_by_username}
            </p>
          </CardContent>
        </Card>

        {/* Crew */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" /> Crew ({report.crew_entries.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {report.crew_entries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No crew recorded</p>
            ) : (
              <div className="space-y-2">
                {report.crew_entries.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between text-sm border rounded-md px-3 py-2"
                  >
                    <div>
                      <p className="font-medium">{c.person_name ?? c.tech_name}</p>
                      {c.role && (
                        <p className="text-xs text-muted-foreground">
                          {c.role}
                        </p>
                      )}
                    </div>
                    {c.hours_on_site && (
                      <Badge variant="secondary" className="text-xs">
                        {c.hours_on_site}h
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Work Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> Work Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p>
              <span className="font-medium">Productive Hours:</span>{' '}
              {report.productive_hours ?? '—'}
            </p>
            <p>
              <span className="font-medium">Notes:</span>{' '}
              {report.notes || '—'}
            </p>
          </CardContent>
        </Card>

        {/* Photos */}
        {report.photos.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Image className="h-4 w-4" /> Photos ({report.photos.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {report.photos.map((p) => (
                  <div key={p.id}>
                    <img
                      src={p.image}
                      alt={p.caption || 'Photo'}
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {p.caption || 'Untitled'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatPhotoMeta(p, timeZone).gps}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatPhotoMeta(p, timeZone).capturedAt}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delays */}
        {report.delay_entries.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> Delays (
                {report.delay_entries.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {report.delay_entries.map((d) => (
                <div
                  key={d.id}
                  className="border rounded-md px-3 py-2 text-sm space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {DELAY_TYPE_LABELS[d.delay_type] ?? d.delay_type}
                    </Badge>
                    {billingEnabled && d.billable && (
                      <Badge
                        variant="outline"
                        className="text-xs text-green-600 border-green-300"
                      >
                        Billable
                      </Badge>
                    )}
                    {d.duration_hours && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        {d.duration_hours}h
                      </span>
                    )}
                  </div>
                  {(d.start_time || d.end_time) && (
                    <p className="text-xs text-muted-foreground">
                      {d.start_time} – {d.end_time}
                    </p>
                  )}
                  {d.description && <p className="text-xs">{d.description}</p>}
                  <p className="text-xs text-muted-foreground">
                    {formatWeatherSnapshot(d.weather_snapshot)}
                  </p>
                  {d.photos && d.photos.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 pt-1">
                      {d.photos.map((p) => (
                        <div key={p.id}>
                          <img
                            src={p.image}
                            alt={p.caption || 'Delay photo'}
                            className="w-full aspect-square object-cover rounded-md"
                          />
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {p.caption || 'Untitled'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatPhotoMeta(p, timeZone).gps}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatPhotoMeta(p, timeZone).capturedAt}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Acknowledgment Status */}
      {billingEnabled && isSubmitted && (
        <div className="px-4 pb-2">
          <Card>
            <CardContent className="py-3">
              {report.client_acknowledged_at ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Acknowledged
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    by {report.acknowledged_by_name} on{' '}
                    {formatDateTimeInTimeZone(report.client_acknowledged_at, timeZone, {
                      dateStyle: 'medium',
                    })}
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                      Not Acknowledged
                    </Badge>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      isLoadingProject ||
                      isLoadingClient ||
                      isLoadingPortalAccesses ||
                      !portalAccessesLoaded ||
                      !client?.contact_email ||
                      createPortalAccess.isPending ||
                      regeneratePortalToken.isPending
                    }
                    onClick={async () => {
                      if (!client?.contact_email) {
                        toast.error('No client contact email for this project.');
                        return;
                      }
                      if (!portalAccessesLoaded) {
                        toast.error('Client portal data is still loading.');
                        return;
                      }
                      try {
                        const existing = portalAccesses.results?.find(
                          (a: ClientPortalAccess) => a.email === client.contact_email
                        );
                        let result: ClientPortalAccess;
                        if (existing) {
                          result = await regeneratePortalToken.mutateAsync(existing.id);
                        } else {
                          result = await createPortalAccess.mutateAsync({
                            client: client.id,
                            email: client.contact_email,
                          });
                        }
                        const url = result.portal_url;
                        if (!url) {
                          toast.error('Portal link was not returned.');
                          return;
                        }
                        setPortalUrl(url);
                        setShowPortalLink(true);
                      } catch {
                        toast.error('Failed to generate portal link.');
                      }
                    }}
                  >
                    <Send className="h-3.5 w-3.5 mr-1" />
                    {createPortalAccess.isPending || regeneratePortalToken.isPending
                      ? 'Generating…'
                      : 'Request Acknowledgment'}
                  </Button>
                </div>
              )}
              {showPortalLink && !report.client_acknowledged_at && portalUrl && (
                <div className="mt-3 p-2 bg-gray-50 rounded-md border text-sm">
                  <p className="text-xs text-muted-foreground mb-1">
                    Share this link with the client:
                  </p>
                  <div className="flex items-center gap-2">
                    <a
                      href={portalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 truncate text-blue-600 underline text-xs"
                    >
                      {portalUrl}
                    </a>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() =>
                        navigator.clipboard.writeText(portalUrl).then(() => toast.success('Link copied'))
                      }
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => window.open(portalUrl, '_blank')}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setShowPortalLink(false)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bottom actions */}
      <div className="sticky bottom-0 bg-white border-t px-4 py-3 space-y-2">
        {/* Export PDF — always available */}
        <Button
          variant="outline"
          className="w-full h-10"
          disabled={exportPdfMutation.isPending}
          onClick={() => exportPdfMutation.mutate(report.id)}
        >
          <Download className="h-4 w-4 mr-2" />
          {exportPdfMutation.isPending ? 'Generating…' : 'Export PDF'}
        </Button>

        {isSubmitted ? (
          /* Amend — only for submitted (locked) reports */
          <Button
            variant="secondary"
            className="w-full h-12 text-base"
            disabled={amendMutation.isPending}
            onClick={() => {
              setAmendReason('');
              setAmendChanges('');
              setAmendDialogOpen(true);
            }}
          >
            <FilePlus className="h-4 w-4 mr-2" />
            {amendMutation.isPending ? 'Creating revision…' : 'Amend Report'}
          </Button>
        ) : (
          /* Edit — only for draft reports */
          <Button
            className="w-full h-12 text-base"
            onClick={() =>
              navigate(
                `/work/daily-reports/${projectId}/${report.id}/edit`
              )
            }
          >
            <Pencil className="h-4 w-4 mr-2" /> Edit Report
          </Button>
        )}
      </div>

      {/* Amend Report Dialog */}
      <Dialog open={amendDialogOpen} onOpenChange={setAmendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Amend Report</DialogTitle>
            <DialogDescription>
              Record a correction for this submitted report. Provide a reason and
              describe what changed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="amend-reason">Reason for amendment *</Label>
              <Textarea
                id="amend-reason"
                value={amendReason}
                onChange={(e) => setAmendReason(e.target.value)}
                placeholder="e.g., incorrect hours logged for crew member"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="amend-changes">Changed fields *</Label>
              <Textarea
                id="amend-changes"
                value={amendChanges}
                onChange={(e) => setAmendChanges(e.target.value)}
                placeholder="Describe the corrections (e.g., crew hours, delay durations, notes)"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAmendDialogOpen(false)}
              disabled={amendMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              disabled={amendMutation.isPending}
              onClick={() =>
                amendMutation.mutate(
                  {
                    id: report.id,
                    reason: amendReason.trim(),
                    changes: { summary: amendChanges.trim() },
                  },
                  {
                    onSuccess: () => {
                      setAmendDialogOpen(false);
                      setAmendReason('');
                      setAmendChanges('');
                      toast.success('Amendment recorded.');
                    },
                    onError: () => {
                      toast.error('Failed to record amendment.');
                    },
                  }
                )
              }
            >
              {amendMutation.isPending ? 'Saving…' : 'Record Amendment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
